/*
 * Work (P2): tasks and follow-ups, My Work, the calendar and the Google
 * Calendar connection, as the real API answers them (TaskController,
 * MyWorkController, CalendarController, RecordController::nextAction).
 *
 * Built from the sample tasks, approvals and calendar in mock/work.js, turned
 * into TaskResource shapes. Writes change the sample in memory.
 */
import { ok, created, fail, invalid, notFound, paginate } from "./util";
import { staff, teams, departments, taskTypes, priorities } from "@/mock/masters";
import { ME, tasks as sampleTasks, relatedKinds, recordsWithoutNextAction, approvals as sampleApprovals, calendarEvents } from "@/mock/work";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
const CALENDAR_TYPES = ["call", "meeting", "demo", "visit", "training", "review"];
const typeLabel = (v) => taskTypes.find((t) => slug(t) === v) ?? (v ? v[0].toUpperCase() + v.slice(1).replace(/_/g, " ") : v);
const priority = (v) => priorities.find((p) => p.key === v) ?? { key: v, name: v, tone: "secondary" };
const staffById = (id) => staff.find((s) => String(s.id) === String(id));
const SOURCES = { manual: "Manual", follow_up: "Next action", system: "System", escalation: "Escalation" };

const related = (r) => {
  if (!r) return null;
  const k = relatedKinds.find((x) => x.key === r.kind);
  return { type: r.kind, id: r.id, code: r.code, name: r.label, label: k?.name ?? r.kind, route: k?.route ?? null };
};

const sourceKey = (s) => (s === "Manual" ? "manual" : /next action/i.test(s) ? "follow_up" : /escalation|breach/i.test(s) ? "escalation" : "system");

const localIso = (day, time) => new Date(`${day}T${time || "23:59"}:00`).toISOString();

/** A sample task in the shape TaskResource gives. */
const fromSample = (t) => {
  const type = slug(t.type);
  const owner = staffById(t.owner_id);
  return {
    id: String(t.id), code: t.code, type, type_label: t.type, title: t.title, description: t.note || null,
    related: related(t.related), owner: owner ? { id: owner.id, name: owner.name } : null, owner_user: null, owner_name: owner?.name ?? null,
    team: null, assigned_by: null,
    due_at: localIso(t.due, t.time), has_time: true, duration_minutes: 30, ends_at: null, location: null, attendees: [], wants_meet_link: false,
    priority: t.priority, status: t.status, source: sourceKey(t.source), source_label: t.source === "Manual" ? "Manual" : t.source,
    is_follow_up: /next action/i.test(t.source), completed_at: t.completed_at ? `${t.completed_at}T12:00:00.000Z` : null, completion_note: null,
    cancelled_at: t.status === "cancelled" ? new Date().toISOString() : null, cancel_reason: t.status === "cancelled" ? t.note : null, reminder_at: null,
    escalation: t.escalated ? { level: t.escalated.level, at: `${t.escalated.at}T09:00:00.000Z`, to: t.escalated.to, rule: t.escalated.rule } : null,
    calendar: null, created_at: `${t.created_at}T08:00:00.000Z`, updated_at: `${t.created_at}T08:00:00.000Z`,
  };
};

const tasks = sampleTasks.map(fromSample);

// Calendar items that are not tasks in the sample (renewals, go-lives) still show on the calendar.
const extraEvents = calendarEvents
  .filter((e) => ["renewal", "golive"].includes(e.type) || !sampleTasks.some((t) => t.title === e.title))
  .map((e) => ({
    ...fromSample({ id: `e${e.id}`, code: `EVT-${e.id}`, type: e.type, title: e.title, related: e.related, owner_id: e.owner_id, due: e.start.slice(0, 10), time: e.all_day ? null : e.start.slice(11, 16), priority: "medium", status: "open", created_at: e.start.slice(0, 10), source: "System" }),
    has_time: !e.all_day,
    duration_minutes: e.end && !e.all_day ? (new Date(e.end) - new Date(e.start)) / 60000 : null,
    location: e.location || null,
    type_label: e.type === "golive" ? "Go-live" : e.type[0].toUpperCase() + e.type.slice(1),
  }));

/** What the resource works out: overdue, end time, priority words, what may be done. */
const present = (t) => {
  const overdue = t.status === "open" && new Date(t.due_at) < new Date();
  const p = priority(t.priority);
  return {
    ...t,
    display_status: overdue ? "overdue" : t.status,
    priority_label: p.name,
    priority_tone: p.tone,
    ends_at: t.has_time ? new Date(new Date(t.due_at).getTime() + (t.duration_minutes || 30) * 60000).toISOString() : null,
    can: { edit: t.status === "open", reassign: t.status === "open" },
  };
};

const find = (id) => tasks.find((t) => t.id === String(id));
const myTeam = () => staff.filter((s) => s.team_id === staffById(ME)?.team_id).map((s) => s.id);
const isMine = (t) => t.owner?.id === ME;

const filtered = (q) => {
  const search = (q.search || "").trim().toLowerCase();
  return tasks
    .map(present)
    .filter((t) => {
      if (q.view === "mine" && !isMine(t)) return false;
      if (q.view === "team" && !myTeam().includes(t.owner?.id)) return false;
      if (q.type && t.type !== q.type) return false;
      if (q.priority && t.priority !== q.priority) return false;
      if (q.owner_id && String(t.owner?.id) !== String(q.owner_id)) return false;
      if (q.related_type && (q.related_type === "none" ? t.related : t.related?.type !== q.related_type)) return false;
      if (q.status === "open" && t.display_status !== "open") return false;
      if (q.status === "overdue" && t.display_status !== "overdue") return false;
      if (q.status === "escalated" && !(t.status === "open" && t.escalation)) return false;
      if (["done", "cancelled"].includes(q.status) && t.status !== q.status) return false;
      if (search && ![t.title, t.code, t.related?.name ?? ""].some((v) => v.toLowerCase().includes(search))) return false;
      return true;
    })
    .sort((a, b) => a.due_at.localeCompare(b.due_at));
};

const count = (q, status) => filtered({ ...q, status }).length;

const parseDue = (value) => {
  const s = String(value || "");
  const hasTime = /\d{1,2}:\d{2}/.test(s);
  return { due_at: new Date(hasTime ? s.replace(" ", "T") : `${s}T23:59:00`).toISOString(), has_time: hasTime };
};

let seq = 1300;

const create = (body) => {
  const errors = {};
  if (!body.title?.trim()) errors.title = ["Give the task a title."];
  if (!body.due_at) errors.due_at = ["Choose when it is due."];
  if (!body.type) errors.type = ["Choose a task type."];
  if (Object.keys(errors).length) return invalid(errors);

  const owner = staffById(body.owner_id ?? ME);
  const { due_at, has_time } = parseDue(body.due_at);
  const rel = body.related_type && body.related_id
    ? tasks.find((t) => t.related?.type === body.related_type && String(t.related?.id) === String(body.related_id))?.related ??
      { type: body.related_type, id: body.related_id, code: null, name: body.related_name ?? "Record", label: relatedKinds.find((k) => k.key === body.related_type)?.name ?? body.related_type, route: null }
    : null;

  const task = {
    ...fromSample({ id: `n${++seq}`, code: `TSK-${new Date().getFullYear()}-${String(seq).padStart(5, "0")}`, type: typeLabel(body.type), title: body.title, related: null, owner_id: owner?.id, due: due_at.slice(0, 10), time: "09:00", priority: body.priority ?? "medium", status: "open", created_at: new Date().toISOString().slice(0, 10), source: "Manual" }),
    type: body.type, type_label: typeLabel(body.type), related: rel, due_at, has_time, description: body.description ?? null,
    duration_minutes: body.duration_minutes ?? (has_time ? 30 : null), location: body.location ?? null, attendees: body.attendees ?? [],
    wants_meet_link: !!body.wants_meet_link, source: body.source ?? "manual", source_label: SOURCES[body.source ?? "manual"], is_follow_up: !!body.is_follow_up,
  };
  tasks.unshift(task);
  return task;
};

const open = (id) => {
  const t = find(id);
  if (!t) return [null, notFound(`Task ${id} was not found.`)];
  if (t.status !== "open") return [null, fail(`${t.code} is ${t.status} and cannot be changed. Reopen it first.`, { code: "CRM_004" }, 409)];
  return [t, null];
};

/* ------------------------------------------------------------------ */
/* Calendar (Google, per person)                                         */
/* ------------------------------------------------------------------ */

const google = {
  provider: "google", configured: true, connected: true, email: "jane.wanjiku@demulla.co.ke", status: "connected",
  last_synced_at: new Date(Date.now() - 120000).toISOString(), last_error: null, connected_at: new Date(Date.now() - 864e5 * 30).toISOString(),
  reminders: [{ method: "email", minutes: 1440 }, { method: "popup", minutes: 60 }, { method: "popup", minutes: 15 }],
  reminder_defaults: [{ method: "email", minutes: 1440 }, { method: "popup", minutes: 60 }, { method: "popup", minutes: 15 }],
};
const googleStatus = () => ({
  ...google,
  message: !google.configured ? "Google Calendar is not configured for this CRM yet." : google.connected ? `Connected as ${google.email}.` : "Not connected.",
});

const day = (n, time) => { const d = new Date(); d.setDate(d.getDate() + n); return new Date(`${d.toISOString().slice(0, 10)}T${time}:00`).toISOString(); };
const busy = [
  { start: day(1, "09:00"), end: day(1, "10:30") },
  { start: day(3, "14:00"), end: day(3, "15:00") },
  { start: day(4, "12:00"), end: day(4, "13:00") },
];

/* ------------------------------------------------------------------ */
/* My Work                                                               */
/* ------------------------------------------------------------------ */

const approvalItems = () => sampleApprovals
  .filter((a) => a.status === "pending" && a.chain[a.current_step - 1]?.approver_id === ME)
  .map((a) => ({
    id: a.id, reference: a.code, workflow: a.type, label: a.summary, requested_by: staffById(a.requester_id)?.name,
    step: a.chain[a.current_step - 1]?.role, sequence: a.current_step, steps: a.chain.length,
    due_at: new Date(Date.now() + (a.sla_hours - a.waiting_hours) * 36e5).toISOString(), past_sla: a.waiting_hours > a.sla_hours, created_at: a.submitted_at,
  }));

let noNext = recordsWithoutNextAction.map((r) => ({ type: r.kind, id: r.id, code: r.code, name: r.label, label: relatedKinds.find((k) => k.key === r.kind)?.name, route: relatedKinds.find((k) => k.key === r.kind)?.route, stage: r.stage, idle_days: r.idle_days }));

/*
 * "View desk of…": the people the prototype's viewer may open (every active
 * member of staff but themselves, and one login with no staff record), and
 * whose desk a request asks for (as_user / as_staff; staff ids stand in for
 * logins here).
 */
const NO_STAFF_LOGIN = { user_id: "u-90", staff_id: null, name: "Patricia Nalyanya", staff_no: null, email: "patricia.nalyanya@demulla.co.ke", team: null, department: null, has_login: true, has_staff: false, hint: "No staff record — nothing can be assigned yet" };
const deskPeople = () => [
  ...staff.filter((s) => s.status === "active" && s.id !== ME).map((s) => {
    const team = teams.find((t) => t.id === s.team_id);
    return { user_id: s.id, staff_id: s.id, name: s.name, staff_no: s.staff_no, email: null, team: team?.name ?? null, department: departments.find((d) => d.id === team?.department_id)?.name ?? null, has_login: true, has_staff: true, hint: null };
  }),
  NO_STAFF_LOGIN,
];
export const deskOf = (query = {}) => {
  const id = query.as_user ?? query.as_staff;
  if (!id || String(id) === String(ME)) return { own: true, read_only: false, user_id: ME, staff_id: ME, name: staffById(ME)?.name ?? "Me", staff_no: staffById(ME)?.staff_no ?? null, has_login: true };
  const p = deskPeople().find((x) => String(x.user_id) === String(id) || String(x.staff_id) === String(id));
  return p ? { own: false, read_only: true, user_id: p.user_id, staff_id: p.staff_id, name: p.name, staff_no: p.staff_no, has_login: p.has_login } : null;
};

const myWork = (query) => {
  const desk = deskOf(query);
  const who = desk.own ? ME : desk.staff_id;
  const mine = tasks.map(present).filter((t) => t.owner?.id === who && t.status === "open" && (!query.related_type || t.related?.type === query.related_type));
  const now = new Date();
  const endToday = new Date(); endToday.setHours(23, 59, 59, 999);
  const endWeek = new Date(endToday.getTime() + 7 * 864e5);
  const at = (t) => new Date(t.due_at);
  const buckets = {
    overdue: mine.filter((t) => at(t) < now),
    today: mine.filter((t) => at(t) >= now && at(t) <= endToday),
    week: mine.filter((t) => at(t) > endToday && at(t) <= endWeek),
    later: mine.filter((t) => at(t) > endWeek),
  };
  Object.values(buckets).forEach((b) => b.sort((a, b2) => a.due_at.localeCompare(b2.due_at)));
  const items = desk.own ? approvalItems() : [];
  const records = desk.own ? noNext : [];
  const startToday = new Date(); startToday.setHours(0, 0, 0, 0);
  return {
    desk,
    counts: { ...Object.fromEntries(Object.entries(buckets).map(([k, v]) => [k, v.length])), escalated: buckets.overdue.filter((t) => t.escalation).length },
    buckets,
    approvals: { count: items.length, past_sla: items.filter((a) => a.past_sla).length, hidden: 0, items },
    without_next_action: { count: records.length, items: records },
    today: [...tasks, ...extraEvents].map(present).filter((t) => t.owner?.id === who && t.has_time && new Date(t.due_at) >= startToday && new Date(t.due_at) <= endToday).sort((a, b) => a.due_at.localeCompare(b.due_at)),
  };
};

export default [
  // ---- Tasks ----
  ["get", "tenant/tasks/meta", () => ok({
    types: taskTypes.map((t) => ({ value: slug(t), label: t })),
    priorities: priorities.map((p) => ({ value: p.key, label: p.name, tone: p.tone })),
    statuses: [{ key: "open", name: "Open", tone: "primary" }, { key: "done", name: "Done", tone: "success" }, { key: "cancelled", name: "Cancelled", tone: "secondary" }],
    sources: Object.entries(SOURCES).map(([key, label]) => ({ key, label })),
    record_types: relatedKinds.filter((k) => k.key !== "renewal").map((k) => ({ type: k.key, label: k.name })),
    calendar_types: CALENDAR_TYPES,
    staff: staff.filter((s) => s.status === "active").map((s) => ({ id: s.id, name: s.name, team_id: s.team_id, user_id: s.id })),
    me: { user_id: ME, staff_id: ME },
    can: { create: true, assign: true, reassign: true },
  })],

  ["get", "tenant/tasks/summary", ({ query }) => ok({
    open: count(query, "open"), overdue: count(query, "overdue"), done: count(query, "done"), cancelled: count(query, "cancelled"), escalated: count(query, "escalated"),
  })],

  ["get", "tenant/tasks", ({ query }) => {
    // A resource collection's page: rows, then links and meta.
    const { data, ...page } = paginate(filtered(query), query, 25);
    return ok({ data, links: {}, meta: { current_page: page.current_page, from: page.from, last_page: page.last_page, per_page: page.per_page, to: page.to, total: page.total } });
  }],

  ["get", "tenant/tasks/:id", ({ params }) => {
    const t = find(params.id);
    return t ? ok({ ...present(t), escalations: t.escalation ? [{ level: t.escalation.level, to: t.escalation.to, target: "supervisor", reason: t.escalation.rule, at: t.escalation.at }] : [] }) : notFound(`Task ${params.id} was not found.`);
  }],

  ["post", "tenant/tasks", ({ body }) => {
    const t = create(body);
    if (Array.isArray(t)) return t;
    return created(present(t), t.owner?.id === ME ? `${t.code} added to your tasks.` : `${t.code} given to ${t.owner?.name}; they have been told.`);
  }],

  ["post", "tenant/tasks/bulk", ({ body }) => {
    let done = 0;
    const skipped = [];
    (body.ids || []).forEach((id) => {
      const t = find(id);
      if (!t || t.status !== "open") return skipped.push({ id, code: t?.code ?? null, reason: t ? `${t.code} is ${t.status}.` : "No such task." });
      if (body.action === "complete") Object.assign(t, { status: "done", completed_at: new Date().toISOString() });
      if (body.action === "cancel") Object.assign(t, { status: "cancelled", cancelled_at: new Date().toISOString(), cancel_reason: body.reason });
      if (body.action === "reschedule") Object.assign(t, parseDue(body.due_at), { escalation: null });
      if (body.action === "reassign") { const s = staffById(body.owner_id); Object.assign(t, { owner: s ? { id: s.id, name: s.name } : t.owner, owner_name: s?.name ?? t.owner_name, escalation: null }); }
      done++;
    });
    return ok({ done, skipped }, `${done} done${skipped.length ? `, ${skipped.length} skipped` : ""}.`);
  }],

  ["put", "tenant/tasks/:id", ({ params, body }) => {
    const [t, error] = open(params.id);
    if (error) return error;
    ["title", "description", "priority", "location", "duration_minutes", "attendees", "wants_meet_link"].forEach((k) => body[k] !== undefined && (t[k] = body[k]));
    if (body.type) Object.assign(t, { type: body.type, type_label: typeLabel(body.type) });
    if (body.due_at) Object.assign(t, parseDue(body.due_at), { escalation: null });
    return ok(present(t), "Task saved.");
  }],

  ["post", "tenant/tasks/:id/complete", ({ params, body }) => {
    const [t, error] = open(params.id);
    if (error) return error;
    Object.assign(t, { status: "done", completed_at: new Date().toISOString(), completion_note: body.note ?? null });
    const needs = t.is_follow_up && !!t.related;
    if (needs && !noNext.some((r) => r.type === t.related.type && String(r.id) === String(t.related.id))) {
      noNext = [{ ...t.related, stage: "—", idle_days: 0 }, ...noNext];
    }
    return ok({ ...present(t), needs_next_action: needs }, needs ? "Done. The record now needs its next action." : "Done.");
  }],

  ["post", "tenant/tasks/:id/reopen", ({ params }) => {
    const t = find(params.id);
    if (!t) return notFound(`Task ${params.id} was not found.`);
    if (t.status === "open") return fail(`${t.code} is already open.`, null, 409);
    Object.assign(t, { status: "open", completed_at: null, cancelled_at: null, cancel_reason: null });
    return ok(present(t), "Reopened.");
  }],

  ["post", "tenant/tasks/:id/cancel", ({ params, body }) => {
    const [t, error] = open(params.id);
    if (error) return error;
    if (!body.reason?.trim()) return invalid({ reason: ["Say why it is being cancelled; it stays on the task."] });
    Object.assign(t, { status: "cancelled", cancelled_at: new Date().toISOString(), cancel_reason: body.reason });
    return ok(present(t), "Cancelled.");
  }],

  ["post", "tenant/tasks/:id/reassign", ({ params, body }) => {
    const [t, error] = open(params.id);
    if (error) return error;
    const s = staffById(body.owner_id);
    if (!s) return invalid({ owner_id: ["Choose who it goes to."] });
    if (t.owner?.id === s.id) return fail(`${t.code} is already theirs.`, null, 409);
    Object.assign(t, { owner: { id: s.id, name: s.name }, owner_name: s.name, escalation: null });
    return ok(present(t), `Now ${s.name}'s; both of you have been told.`);
  }],

  // ---- My Work ----
  ["get", "tenant/my-work", ({ query }) => (deskOf(query) ? ok(myWork(query)) : fail("There is nobody with that id.", null, 404))],
  ["get", "tenant/my-work/desks", ({ query }) => {
    const search = String(query.search ?? "").trim().toLowerCase();
    return ok(deskPeople().filter((p) => !search || [p.name, p.staff_no, p.email].some((v) => String(v ?? "").toLowerCase().includes(search))).slice(0, Number(query.limit) || 50));
  }],

  ["post", "tenant/records/:type/:id/next-action", ({ params, body }) => {
    const rec = noNext.find((r) => r.type === params.type && String(r.id) === String(params.id));
    const t = create({ ...body, title: body.title ?? `${typeLabel(body.type)}: ${rec?.name ?? "record"}`, related_type: params.type, related_id: params.id, related_name: rec?.name, source: "follow_up", is_follow_up: true });
    if (Array.isArray(t)) return t;
    if (rec) t.related = { type: rec.type, id: rec.id, code: rec.code, name: rec.name, label: rec.label, route: rec.route };
    noNext = noNext.filter((r) => r !== rec);
    return ok(present(t), "Next action set.");
  }],

  // ---- Calendar ----
  ["get", "tenant/calendar/status", () => ok(googleStatus())],

  ["get", "tenant/calendar/events", ({ query }) => {
    const from = new Date(query.from), to = new Date(query.to);
    const events = [...tasks, ...extraEvents].map(present)
      .filter((t) => t.status !== "cancelled" && new Date(t.due_at) >= from && new Date(t.due_at) <= to)
      .filter((t) => !query.owner_id || String(t.owner?.id) === String(query.owner_id))
      .filter((t) => !(query.mine === true || query.mine === "1" || query.mine === 1) || t.owner?.id === ME);
    const wantBusy = [true, "1", 1, "true"].includes(query.busy) && google.connected;
    return ok({ events, busy: wantBusy ? busy : [], google: googleStatus() });
  }],

  ["get", "tenant/calendar/free-busy", () => ok({})],

  ["get", "tenant/calendar/google/connect", () => {
    if (!google.configured) return fail("Google Calendar is not configured for this CRM yet.", { code: "CRM_040", details: { reason: "not_configured" } }, 409);
    // Prototype: no real Google; come straight back connected.
    google.connected = true;
    google.status = "connected";
    return ok({ url: `${globalThis.location?.pathname ?? "/activities/calendar"}?google=connected` });
  }],

  ["post", "tenant/calendar/disconnect", () => {
    Object.assign(google, { connected: false, status: "not_connected", email: null });
    return ok(googleStatus(), "Disconnected. Your appointments will go to guests as email invites instead.");
  }],

  ["put", "tenant/calendar/reminders", ({ body }) => {
    if (!google.connected) return fail("Connect your Google Calendar first.", { code: "CRM_040", details: { reason: "not_connected" } }, 409);
    google.reminders = body.reminders?.length ? body.reminders : google.reminder_defaults;
    return ok({ reminders: google.reminders }, "Reminders saved.");
  }],

  ["post", "tenant/calendar/sync", () => {
    google.last_synced_at = new Date().toISOString();
    const n = tasks.filter((t) => t.owner?.id === ME && t.status === "open" && t.has_time && new Date(t.due_at) > new Date()).length;
    return ok({ queued: n }, n ? `${n} upcoming appointment(s) queued for your calendar.` : "Nothing upcoming to sync.");
  }],
];

